import { Breakpoint } from '@mui/material/styles';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useWidth from '../../../../components/Hooks/useWidth';
import { GetLimit, GetStockValue, ProductLockProps } from '../../../../hooks/useProductLock.types';
import { getInputLimit } from '../../../../hooks/useProductLock.utils';
import useGeneralSettingsStore from '../../../../stores/GeneralSettings/useGeneralSettingsStore';

interface ProductLockProviderProps {
  children: React.ReactNode;
  isEnabledOnTablet?: boolean;
}

const ProductDetailsLockContext = createContext<ProductLockProps | undefined>(undefined);

const DESKTOP_LOCK_BREAKPOINTS: Breakpoint[] = ['xl', 'lg', 'md'];
const TABLET_LOCK_BREAKPOINT: Breakpoint = 'sm';

export const ProductLockProvider = ({ children, isEnabledOnTablet }: ProductLockProviderProps) => {
  const { activeBreakpoint } = useWidth();
  const enabledBreakpoints = isEnabledOnTablet
    ? [...DESKTOP_LOCK_BREAKPOINTS, TABLET_LOCK_BREAKPOINT]
    : DESKTOP_LOCK_BREAKPOINTS;
  const isLockEnabled = enabledBreakpoints.includes(activeBreakpoint);

  const [isLocked, setIsLocked] = useState(true);

  const { productInputLockValue } = useGeneralSettingsStore(
    useShallow((state) => ({
      productInputLockValue: state.generalSettings.productInputLockValue,
    })),
  );

  const onClickLock = useCallback(() => setIsLocked(!isLocked), [isLocked]);

  const getLimit: GetLimit = ({ availableAmount, availability }) =>
    getInputLimit({
      isLockEnabled,
      isLocked,
      availability,
      availableAmount,
      lockValue: productInputLockValue,
    });

  const getStockValue: GetStockValue = ({ availableAmount, availability }) =>
    getInputLimit({
      isLockEnabled,
      availability,
      availableAmount,
      isLocked: false,
      lockValue: productInputLockValue,
    });

  const contextValue: ProductLockProps = {
    isLocked,
    isLockEnabled,
    onClickLock,
    getLimit,
    getStockValue,
  };

  return (
    <ProductDetailsLockContext.Provider value={contextValue}>
      {children}
    </ProductDetailsLockContext.Provider>
  );
};

export const useProductLock = () => {
  const context = useContext(ProductDetailsLockContext);
  if (!context) {
    throw new Error('useProductDetailsLock must be used within a ProductDetailsLockProvider');
  }
  return context;
};
